import React, { useEffect, useRef, useState } from "react";
import toast from "react-hot-toast";
import { hasBarcode, barcodeIncludes, barcodeCouldGrow } from "../../../core/utils/productBarcode";
import { Sliders } from "feather-icons-react/build/IconComponents";

/* ==========================================================================
   Item entry for the transaction terminals - the Sales Invoice scan bar,
   entry flow (direct / qty / qty -> price), barcode lookup and keyboard
   handling, shared so every sales and purchase screen behaves the same.

   Lines are still created through each screen's own handleAddRow and
   handleEachProductfetch, so unit, tax and price load exactly as they
   always have on that screen.
   ========================================================================== */

const readPref = (key, fallback) => {
  try {
    const raw = window.localStorage.getItem(key);
    return raw ? { ...fallback, ...JSON.parse(raw) } : fallback;
  } catch {
    return fallback;
  }
};

const writePref = (key, value) => {
  try {
    window.localStorage.setItem(key, JSON.stringify(value));
  } catch {
    /* storage unavailable - the choice just will not persist */
  }
};

const num = (v) => {
  if (v === null || v === undefined || v === "") return 0;
  const n = Number(v);
  return isNaN(n) ? 0 : n;
};

// An untouched row - no product, no quantity, no price.
export const isBlankRow = (row) =>
  !row?.product && !(Number(row?.quantity) > 0) && !(Number(row?.price) > 0);

export const FLOW_OPTIONS = [
  {
    v: "direct",
    t: "Straight to the grid",
    h: "Picking an item adds it with quantity 1. Fastest for barcode scanning.",
  },
  {
    v: "qty",
    t: "Ask quantity, then add",
    h: "Control moves to Qty. Enter adds the line at the default rate.",
  },
  {
    v: "qtyprice",
    t: "Ask quantity, then price",
    h: "Qty → Enter → Price → Enter adds the line. Use when rates are negotiated.",
  },
];

/**
 * @param {object} o
 * @param {string} o.screenKey              prefix for saved preferences
 * @param {Array}  o.productList            redux product list
 * @param {Array}  o.unitOptions            [{ label, value }] unit options
 * @param {Function} o.getValues            react-hook-form
 * @param {Function} o.setValue             react-hook-form
 * @param {Function} o.handleAddRow         the screen's own add-row
 * @param {Function} o.handleEachProductfetch the screen's own product loader
 * @param {Function} o.rateOf               product -> default rate for this screen
 * @param {Function} [o.removeLine]         index -> removes a line (Ctrl+Del)
 * @param {boolean}  [o.blocked]            true while another modal is on top
 */
export const useItemEntry = ({
  screenKey,
  productList,
  unitOptions,
  getValues,
  setValue,
  handleAddRow,
  handleEachProductfetch,
  rateOf,
  removeLine,
  blocked = false,
}) => {
  const flowKey = `${screenKey}.entryFlow`;
  const [entryFlow, setEntryFlowState] = useState(
    () => readPref(flowKey, { mode: "qtyprice" }).mode
  );
  const [flowOpen, setFlowOpen] = useState(false);
  const [scanText, setScanText] = useState("");
  const [scanSel, setScanSel] = useState(0);
  const [scanOpen, setScanOpen] = useState(false);
  const [pending, setPending] = useState(null);
  const [entryQty, setEntryQty] = useState("1");
  const [entryPrice, setEntryPrice] = useState("0");
  const [armed, setArmed] = useState(null); // "qty" | "price" | null
  const [lastAdded, setLastAdded] = useState(null);
  const scanRef = useRef(null);
  const qtyRef = useRef(null);
  const priceRef = useRef(null);

  const focusScan = (select) =>
    setTimeout(() => {
      scanRef.current?.focus();
      if (select) scanRef.current?.select();
    }, 0);

  const productByGuid = (guid) =>
    guid ? productList?.find((p) => p.guid === guid) : undefined;
  const unitLabel = (guid) =>
    unitOptions?.find((u) => u.value === guid)?.label || "";

  const scanQuery = scanText.trim().toLowerCase();
  const scanResults =
    !scanQuery || pending
      ? []
      : (productList || [])
          .filter(
            (p) =>
              `${p?.name ?? ""}`.toLowerCase().includes(scanQuery) ||
              `${p?.code ?? ""}`.toLowerCase().includes(scanQuery) ||
              barcodeIncludes(p, scanQuery)
          )
          .slice(0, 8);

  const exactProduct = (text) => {
    const t = `${text ?? ""}`.trim();
    if (!t) return null;
    return (
      productList?.find(
        (p) => hasBarcode(p, t) || `${p?.code ?? ""}`.trim() === t
      ) || null
    );
  };

  function resetEntry() {
    setPending(null);
    setScanText("");
    setScanSel(0);
    setEntryQty("1");
    setEntryPrice("0");
    setArmed(null);
    focusScan();
  }

  const setEntryFlow = (mode) => {
    setEntryFlowState(mode);
    writePref(flowKey, { mode });
    setFlowOpen(false);
    resetEntry();
  };

  // A product already on the document gets its quantity raised instead of a
  // second line; otherwise the trailing blank line is filled (one is appended
  // when there is none) through the screen's own product loader.
  async function addLine(product, qty, price) {
    const q = Number(qty) > 0 ? Number(qty) : 1;
    const rows = getValues("items") || [];
    const existing = rows.findIndex((r) => r?.product === product.guid);
    if (existing > -1) {
      setValue(`items[${existing}].quantity`, num(rows[existing].quantity) + q);
      if (price !== undefined) setValue(`items[${existing}].price`, price);
    } else {
      let index = rows.length - 1;
      if (index < 0 || !isBlankRow(rows[index])) {
        handleAddRow();
        index = rows.length;
      }
      await handleEachProductfetch(
        { value: product.guid, label: product.name },
        index
      );
      setValue(`items[${index}].quantity`, q);
      if (price !== undefined) setValue(`items[${index}].price`, price);
    }
    setLastAdded({
      name: product.name,
      qty: q,
      unit: unitLabel(product.unit),
      price: price !== undefined ? price : num(rateOf(product)),
    });
    setTimeout(() => scanRef.current?.focus(), 40);
  }

  function beginEntry(product) {
    if (!product) return;
    setScanOpen(false);
    setScanSel(0);
    if (entryFlow === "direct") {
      addLine(product, 1);
      resetEntry();
      return;
    }
    setPending(product);
    setScanText(product.name || "");
    setEntryQty("1");
    setEntryPrice(`${num(rateOf(product))}`);
    setArmed("qty");
    setTimeout(() => {
      qtyRef.current?.focus();
      qtyRef.current?.select();
    }, 0);
  }

  function commitEntry() {
    if (!pending) return;
    const q = num(entryQty);
    const p = entryFlow === "qtyprice" ? num(entryPrice) : undefined;
    addLine(pending, q > 0 ? q : 1, p);
    resetEntry();
  }

  const onScanChange = (e) => {
    const v = e.target.value;
    setScanText(v);
    setScanSel(0);
    setScanOpen(true);
    if (pending) {
      setPending(null);
      setArmed(null);
    }
    // A scanner types the whole code then Enter. A full-length barcode that no
    // longer barcode could still grow into is taken at once; anything shorter
    // waits for Enter, so a short item code can't fire on the first keystroke.
    const t = v.trim();
    if (t.length >= 8) {
      const hit = productList?.find((p) => hasBarcode(p, t));
      const longer = productList?.some((p) => barcodeCouldGrow(p, t));
      if (hit && !longer) beginEntry(hit);
    }
  };

  const onScanKeyDown = (e) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setScanOpen(true);
      setScanSel((s) => Math.min(s + 1, Math.max(scanResults.length - 1, 0)));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setScanSel((s) => Math.max(s - 1, 0));
    } else if (e.key === "Enter") {
      e.preventDefault();
      if (pending) {
        qtyRef.current?.focus();
        return;
      }
      const hit = exactProduct(scanText);
      if (hit) beginEntry(hit);
      else if (scanResults[scanSel]) beginEntry(scanResults[scanSel]);
      else if (scanText.trim())
        toast.error(`No item found for "${scanText.trim()}"`);
    } else if (e.key === "Escape") {
      e.preventDefault();
      resetEntry();
    }
  };

  const onEntryQtyKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      if (!pending) {
        scanRef.current?.focus();
        return;
      }
      if (entryFlow === "qtyprice") {
        setArmed("price");
        setTimeout(() => {
          priceRef.current?.focus();
          priceRef.current?.select();
        }, 0);
      } else commitEntry();
    } else if (e.key === "Escape") {
      e.preventDefault();
      resetEntry();
    }
  };

  const onEntryPriceKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      commitEntry();
    } else if (e.key === "Escape") {
      e.preventDefault();
      resetEntry();
    }
  };

  // Barcode typed or scanned into a line's barcode cell.
  const lookupBarcode = (rawValue, index) => {
    const scanned = `${rawValue ?? ""}`.trim();
    if (!scanned) return;
    const current = productByGuid(getValues(`items[${index}].product`));
    if (
      current &&
      (hasBarcode(current, scanned) ||
        `${current.code ?? ""}`.trim() === scanned)
    )
      return;
    const match = exactProduct(scanned);
    if (!match) {
      setValue(
        `items[${index}].code`,
        `${current?.barcode || current?.code || ""}`
      );
      toast.error(`No item found for barcode ${scanned}`);
      return;
    }
    handleEachProductfetch({ value: match.guid, label: match.name }, index);
  };

  // lets an item picker be searched by name, barcode or item code
  const productFilterOption = (option, rawInput) => {
    const input = `${rawInput ?? ""}`.trim().toLowerCase();
    if (!input) return true;
    const p = productByGuid(option.value) || {};
    return (
      `${option.label ?? ""}`.toLowerCase().includes(input) ||
      barcodeIncludes(p, input) ||
      `${p.code ?? ""}`.toLowerCase().includes(input)
    );
  };

  // F2 party, F3 scanner, F9 save & print. Re-bound every render so the
  // handler always sees current state; stands down while a modal is on top.
  useEffect(() => {
    const onKey = (e) => {
      if (blocked) return;
      if (e.key === "F2") {
        const el = document.querySelector(".si-term [data-term-party] input");
        if (el) {
          e.preventDefault();
          el.focus();
        }
      } else if (e.key === "F3") {
        e.preventDefault();
        focusScan(true);
      } else if (e.key === "F9") {
        const btn = document.querySelector(
          '.si-term [data-term-action="save-print"]'
        );
        if (btn) {
          e.preventDefault();
          btn.click();
        }
      }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  });

  // Enter never submits by accident; inside a line it hands control back to
  // the scanner. Ctrl+Delete removes the line.
  const onFormKeyDown = (e) => {
    const t = e.target;
    if (!(t instanceof HTMLInputElement)) return;
    const inLine = t.closest(".si-table");
    if (e.key === "Delete" && e.ctrlKey && inLine && removeLine) {
      const tr = t.closest("tr[data-line]");
      if (tr) {
        e.preventDefault();
        removeLine(Number(tr.dataset.line));
        focusScan();
      }
      return;
    }
    if (e.key !== "Enter" || e.defaultPrevented) return;
    if (t.type === "checkbox" || t.type === "radio") return;
    e.preventDefault();
    if (inLine) focusScan(true);
  };

  return {
    entryFlow,
    setEntryFlow,
    flowOpen,
    setFlowOpen,
    scanText,
    scanSel,
    setScanSel,
    scanOpen,
    setScanOpen,
    scanResults,
    pending,
    entryQty,
    setEntryQty,
    entryPrice,
    setEntryPrice,
    armed,
    lastAdded,
    scanRef,
    qtyRef,
    priceRef,
    beginEntry,
    onScanChange,
    onScanKeyDown,
    onEntryQtyKeyDown,
    onEntryPriceKeyDown,
    lookupBarcode,
    productFilterOption,
    productByGuid,
    unitLabel,
    rateOf,
    onFormKeyDown,
    resetEntry,
  };
};

const barcodeIcon = (
  <svg
    viewBox="0 0 16 16"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.6"
    aria-hidden="true"
  >
    <path d="M2 4V2.6h1.6M14 4V2.6h-1.6M2 12v1.4h1.6M14 12v1.4h-1.6" />
    <path d="M5 5v6M7 5v6M9.2 5v6M11.4 5v6" />
  </svg>
);

/** Scan bar, search results, entry-flow popover and the "last added" echo. */
export const TermScanBar = ({ entry, fmt, rateLabel = "Rate" }) => (
  <>
    <div className="si-scanbar">
      <div className="si-scanwrap">
        {barcodeIcon}
        <input
          ref={entry.scanRef}
          className="si-scan"
          value={entry.scanText}
          onChange={entry.onScanChange}
          onKeyDown={entry.onScanKeyDown}
          onFocus={() => entry.setScanOpen(true)}
          onBlur={() => entry.setScanOpen(false)}
          autoComplete="off"
          placeholder="Scan barcode or type item name…"
          aria-label="Scan barcode or search items"
        />
        <span className="si-kbd si-scan-kbd">F3</span>
      </div>

      {entry.entryFlow !== "direct" && (
        <div
          className={`si-entryfld${
            entry.armed === "qty" ? " si-entryfld--armed" : ""
          }`}
        >
          <label htmlFor="si-entry-qty">Qty</label>
          <input
            id="si-entry-qty"
            ref={entry.qtyRef}
            inputMode="decimal"
            autoComplete="off"
            value={entry.entryQty}
            onChange={(e) => entry.setEntryQty(e.target.value)}
            onKeyDown={entry.onEntryQtyKeyDown}
          />
        </div>
      )}

      {entry.entryFlow === "qtyprice" && (
        <div
          className={`si-entryfld si-entryfld--wide${
            entry.armed === "price" ? " si-entryfld--armed" : ""
          }`}
        >
          <label htmlFor="si-entry-price">Price</label>
          <input
            id="si-entry-price"
            ref={entry.priceRef}
            inputMode="decimal"
            autoComplete="off"
            value={entry.entryPrice}
            onChange={(e) => entry.setEntryPrice(e.target.value)}
            onKeyDown={entry.onEntryPriceKeyDown}
          />
        </div>
      )}

      <button
        type="button"
        className="si-iconbtn si-iconbtn--tall"
        aria-expanded={entry.flowOpen}
        title="Item entry flow"
        aria-label="Item entry flow settings"
        onClick={() => entry.setFlowOpen((o) => !o)}
      >
        <Sliders />
      </button>

      {entry.scanOpen && entry.scanResults.length > 0 && (
        <div className="si-results" onMouseDown={(e) => e.preventDefault()}>
          <div className="si-res-head">
            <span>Item</span>
            <span>Unit</span>
            <span>{rateLabel}</span>
            <span>MRP</span>
          </div>
          {entry.scanResults.map((p, i) => (
            <div
              key={p.guid}
              className={`si-res${i === entry.scanSel ? " si-res--sel" : ""}`}
              onMouseEnter={() => entry.setScanSel(i)}
              onClick={() => entry.beginEntry(p)}
            >
              <div className="si-res-item">
                <div className="nm">{p.name}</div>
                <div className="cd">
                  {[p.code, p.barcode].filter(Boolean).join(" · ")}
                </div>
              </div>
              <div className="si-res-m">{entry.unitLabel(p.unit) || "—"}</div>
              <div className="si-res-m">
                <b>{fmt(entry.rateOf(p))}</b>
              </div>
              <div className="si-res-m">
                {num(p.mrp) > 0 ? fmt(p.mrp) : "—"}
              </div>
            </div>
          ))}
        </div>
      )}

      {entry.flowOpen && (
        <>
          <div
            className="si-pop-scrim si-pop-scrim--clear"
            onClick={() => entry.setFlowOpen(false)}
          />
          <div className="si-pop si-flowpop" role="dialog">
            <div className="si-pop-h">Item entry flow</div>
            {FLOW_OPTIONS.map((o) => (
              <label className="si-opt" key={o.v}>
                <input
                  type="radio"
                  name={`si-entry-flow`}
                  checked={entry.entryFlow === o.v}
                  onChange={() => entry.setEntryFlow(o.v)}
                />
                <span>
                  <b>{o.t}</b>
                  <i>{o.h}</i>
                </span>
              </label>
            ))}
          </div>
        </>
      )}
    </div>

    {entry.lastAdded && (
      <div className="si-lastline">
        <span className="si-tag">Last added</span>
        <b>{entry.lastAdded.name}</b>
        <span className="si-q">
          × {entry.lastAdded.qty}
          {entry.lastAdded.unit ? ` ${entry.lastAdded.unit}` : ""} @{" "}
          {fmt(entry.lastAdded.price)}
        </span>
      </div>
    )}
  </>
);

/**
 * Product facts shown in a line's detail row.
 * Margin is (sell - buy) / sell: on a sale the line's rate against the item
 * cost, on a purchase the item's retail rate against the line's rate.
 */
export const TermLineFacts = ({ product, sell, buy, fmt }) => {
  if (!product) return null;
  const cost = num(product.cost);
  const s = num(sell);
  const b = num(buy);
  const margin = s > 0 && b > 0 ? ((s - b) / s) * 100 : null;
  return (
    <div className="si-facts">
      {product.code && (
        <div className="si-fact">
          <div className="k">Item code</div>
          <div className="v">{product.code}</div>
        </div>
      )}
      {product.barcode && (
        <div className="si-fact">
          <div className="k">Barcode</div>
          <div className="v">{product.barcode}</div>
        </div>
      )}
      <div className="si-fact">
        <div className="k">List rate</div>
        <div className="v">{fmt(product.retailRate)}</div>
      </div>
      {cost > 0 && (
        <div className="si-fact">
          <div className="k">Cost</div>
          <div className="v">{fmt(cost)}</div>
        </div>
      )}
      {num(product.mrp) > 0 && (
        <div className="si-fact">
          <div className="k">MRP</div>
          <div className="v">{fmt(product.mrp)}</div>
        </div>
      )}
      {margin !== null && (
        <div
          className={`si-fact ${margin < 8 ? "si-fact--warn" : "si-fact--good"}`}
        >
          <div className="k">Margin</div>
          <div className="v">{margin.toFixed(1)}%</div>
        </div>
      )}
    </div>
  );
};
